import React from "react";
import { Box, Card, CardContent, Stack, Typography, styled } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import type { ScheduledReportOverview } from "@/app2/shared/data";
import { CustomTooltip } from "@/components/common";
import { useSSTranslation } from "@/i18n/useSSTranslation";
import type { II18nMap } from "@/i18n/en";
import { formatHours } from "./utils";

type ScheduledReportsOverviewTooltipKey = keyof Pick<
  II18nMap,
  | "scheduled_reports_tooltip_total_watch_time"
  | "scheduled_reports_tooltip_users_with_completed_lessons"
  | "scheduled_reports_tooltip_average_watch_time"
  | "scheduled_reports_tooltip_completed_lessons"
  | "scheduled_reports_tooltip_completed_courses"
  | "scheduled_reports_tooltip_average_streak"
  | "scheduled_reports_tooltip_overdue_lessons"
  | "scheduled_reports_tooltip_utilization"
>;

const OverviewCard = styled(Card)(({ theme }) => ({
  borderRadius: "8px",
  backgroundColor: theme.palette.background.paper,
  height: "138px",
  border: "1px solid #A1A1A026",
  boxSizing: "border-box",
}));

function renderCard(
  title: string,
  value: string | number,
  changePercent: number | undefined,
  tooltipKey: ScheduledReportsOverviewTooltipKey,
  t: (key: keyof II18nMap, fallback: string) => string,
  reverseColors = false
) {
  const hasChange = changePercent != null && changePercent !== 0;
  const isPositive = hasChange && changePercent! > 0;
  const isNegative = hasChange && changePercent! < 0;
  const changeColor = reverseColors
    ? isPositive ? "#f44336" : isNegative ? "#4caf50" : undefined
    : isPositive ? "#4caf50" : isNegative ? "#f44336" : undefined;
  const isWatchTime = typeof value === "string" && String(value).includes("hrs");
  return (
    <OverviewCard key={title} sx={{ flex: { xs: "1 1 30%", lg: "1 1 22%" } }}>
      <CardContent sx={{ height: "95px", position: "relative", padding: "16px !important" }}>
        <Box sx={{ position: "absolute", top: "20px", left: "16px", right: "16px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <CustomTooltip title={t(tooltipKey, "")}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, pr: 1, cursor: "help" }}>
              {title.toUpperCase()}
            </Typography>
          </CustomTooltip>
          {hasChange && (
            <Stack direction="row" gap={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
              {isPositive && <ArrowUpwardIcon sx={{ color: changeColor, fontSize: 16 }} />}
              {isNegative && <ArrowDownwardIcon sx={{ color: changeColor, fontSize: 16 }} />}
              <Typography variant="body2" sx={{ color: changeColor, fontWeight: 600, whiteSpace: "nowrap" }}>
                {Math.round(Math.abs(changePercent!))}%
              </Typography>
            </Stack>
          )}
        </Box>
        <Box sx={{ position: "absolute", bottom: "12px", width: "100%" }}>
          <Typography variant="h2" color="primary" sx={{ fontWeight: 600, fontSize: isWatchTime ? "2rem" : undefined }}>
            {value}
          </Typography>
        </Box>
      </CardContent>
    </OverviewCard>
  );
}

export interface ScheduledReportsOverviewCardsProps {
  overviewData: ScheduledReportOverview;
}

export const ScheduledReportsOverviewCards: React.FC<ScheduledReportsOverviewCardsProps> = ({ overviewData }) => {
  const { t } = useSSTranslation();

  return (
    <Stack direction="row" flexWrap="wrap" sx={{ gap: 2 }}>
      {renderCard(
        "Total Watch Time",
        formatHours(Number(overviewData["Total Watch Time (hours)"]) || 0),
        overviewData["Watch Time Change %"] as number,
        "scheduled_reports_tooltip_total_watch_time",
        t
      )}
      {renderCard(
        "Users with Completed Lessons",
        String(overviewData["Users with Completed Lessons"] ?? 0),
        overviewData["Users with Completed Lessons Change %"] as number,
        "scheduled_reports_tooltip_users_with_completed_lessons",
        t
      )}
      {renderCard(
        "Average Watch Time",
        formatHours(Number(overviewData["Average Watch Time (hours)"]) || 0),
        overviewData["Average Watch Time Change %"] as number,
        "scheduled_reports_tooltip_average_watch_time",
        t
      )}
      {renderCard(
        "Completed Lessons",
        Number(overviewData["Completed Lessons"]) ?? 0,
        overviewData["Completed Lessons Change %"] as number,
        "scheduled_reports_tooltip_completed_lessons",
        t
      )}
      {renderCard(
        "Completed Courses",
        Number(overviewData["Completed Courses"]) ?? 0,
        overviewData["Completed Courses Change %"] as number,
        "scheduled_reports_tooltip_completed_courses",
        t
      )}
      {renderCard(
        "Average Streak",
        `${overviewData["Average Streak (days)"] ?? 0} days`,
        overviewData["Average Streak Change %"] as number,
        "scheduled_reports_tooltip_average_streak",
        t
      )}
      {renderCard(
        "Overdue Lessons",
        Number(overviewData["Overdue Lessons"]) ?? 0,
        overviewData["Overdue Lessons Change %"] as number,
        "scheduled_reports_tooltip_overdue_lessons",
        t,
        true
      )}
      {renderCard(
        "Utilization",
        `${overviewData["Utilization %"] ?? 0}%`,
        overviewData["Utilization Change %"] as number,
        "scheduled_reports_tooltip_utilization",
        t
      )}
    </Stack>
  );
};
